import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip as MuiTooltip,
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import type { RealtimeStockLatestResponse } from './RealtimeTickerDrawer';

type Props = {
  items: RealtimeStockLatestResponse[];
  updatedTickers: Record<string, number>;
  isMobile?: boolean;
  onOpenQuick: (item: RealtimeStockLatestResponse) => void;
};

const neo = {
  border: 'rgba(120,140,180,.22)',
  border2: 'rgba(120,140,180,.14)',
  text: '#eaf2ff',
  muted: '#9fb4d8',
  soft: '#7f9cc3',
  pillBg: 'rgba(120,140,180,.10)',
  green: '#7DFFA7',
  greenBg: 'rgba(61,220,151,.10)',
  greenBd: 'rgba(61,220,151,.30)',
  red: '#FF9E9E',
  redBg: 'rgba(255,143,143,.10)',
  redBd: 'rgba(255,143,143,.30)',
  yellow: '#fde68a',
  yellowBg: 'rgba(250,204,21,.10)',
  yellowBd: 'rgba(250,204,21,.28)',
};

function formatNumber(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat('ko-KR').format(value);
}

function formatSignedNumber(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${new Intl.NumberFormat('ko-KR').format(value)}`;
}

function formatPercent(value?: number | null, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(digits)}%`;
}

function formatTradingValue(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const abs = Math.abs(value);
  if (abs >= 100000000) return `${(value / 100000000).toFixed(1)}억`;
  if (abs >= 10000) return `${(value / 10000).toFixed(0)}만`;
  return formatNumber(value);
}

function formatRelativeTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const diffMs = Date.now() - date.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}초 전`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour}시간 전`;
  return value.replace('T', ' ').slice(0, 19);
}

function getChangeColor(value?: number | null) {
  if (value === null || value === undefined) return neo.text;
  if (value > 0) return neo.green;
  if (value < 0) return neo.red;
  return neo.text;
}

function getUpdatedTone(lastEventAt?: string | null): React.CSSProperties {
  if (!lastEventAt) {
    return {
      color: '#cbd5e1',
      background: 'rgba(148,163,184,.10)',
      border: '1px solid rgba(148,163,184,.20)',
    };
  }

  const diffMs = Date.now() - new Date(lastEventAt).getTime();
  if (diffMs <= 3 * 60 * 1000) {
    return {
      color: neo.green,
      background: neo.greenBg,
      border: `1px solid ${neo.greenBd}`,
    };
  }
  if (diffMs <= 10 * 60 * 1000) {
    return {
      color: neo.yellow,
      background: neo.yellowBg,
      border: `1px solid ${neo.yellowBd}`,
    };
  }
  return {
    color: '#cbd5e1',
    background: 'rgba(148,163,184,.10)',
    border: '1px solid rgba(148,163,184,.20)',
  };
}

function getVelocityTone(changeRate?: number | null, tradingValue?: number | null) {
  const cr = changeRate ?? 0;
  const tv = tradingValue ?? 0;

  if (cr >= 5 || tv >= 10000000000) {
    return {
      label: '강한 움직임',
      color: neo.green,
      bg: neo.greenBg,
      bd: neo.greenBd,
    };
  }
  if (cr >= 2 || tv >= 3000000000) {
    return {
      label: '관찰 필요',
      color: neo.yellow,
      bg: neo.yellowBg,
      bd: neo.yellowBd,
    };
  }
  return {
    label: '일반 흐름',
    color: neo.text,
    bg: neo.pillBg,
    bd: neo.border,
  };
}

const MiniStat: React.FC<{
  label: string;
  value: string;
  tone?: number | null;
}> = ({ label, value, tone = null }) => {
  const color =
    tone == null ? neo.text : tone > 0 ? neo.green : tone < 0 ? neo.red : neo.text;

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1.5,
        border: `1px solid ${neo.border2}`,
        background: 'rgba(255,255,255,.02)',
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 11, color: neo.soft, fontWeight: 700 }}>{label}</Typography>
      <Typography
        sx={{
          mt: 0.5,
          fontSize: 14,
          color,
          fontWeight: 900,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' | 'center' }> = ({
  children,
  align = 'left',
}) => (
  <th
    style={{
      textAlign: align,
      padding: '14px 14px',
      fontSize: 12,
      color: neo.soft,
      fontWeight: 800,
      borderBottom: `1px solid ${neo.border2}`,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </th>
);

const Td: React.FC<{
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  changed?: boolean;
  color?: string;
  strong?: boolean;
}> = ({ children, align = 'left', changed = false, color, strong = false }) => (
  <td
    style={{
      textAlign: align,
      padding: '14px 14px',
      fontSize: strong ? 14 : 13,
      color: color || (strong ? '#f8fbff' : neo.text),
      borderBottom: `1px solid ${neo.border2}`,
      whiteSpace: 'nowrap',
      fontWeight: strong ? 900 : 500,
      background: changed ? 'rgba(79,124,255,.08)' : 'transparent',
      transition: 'background .18s ease',
    }}
  >
    {children}
  </td>
);

export default function RealtimeMiniBoard({
  items,
  updatedTickers,
  isMobile = false,
  onOpenQuick,
}: Props) {
  if (!items.length) {
    return (
      <Box
        sx={{
          borderRadius: 2,
          border: `1px solid ${neo.border}`,
          background: 'rgba(255,255,255,.02)',
          p: 2,
        }}
      >
        <Typography sx={{ color: neo.muted, fontSize: 14 }}>
          표시할 실시간 종목이 없습니다.
        </Typography>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box sx={{ display: 'grid', gap: 1 }}>
        {items.map((item) => {
          const changed = Boolean(updatedTickers[item.ticker]);
          const velocity = getVelocityTone(item.changeRate, item.cumTradingValue);

          return (
            <Box
              key={item.ticker}
              onClick={() => onOpenQuick(item)}
              sx={{
                p: 1.4,
                borderRadius: 2,
                border: `1px solid ${neo.border}`,
                background: changed ? 'rgba(79,124,255,.08)' : 'rgba(255,255,255,.02)',
                cursor: 'pointer',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 900,
                        color: '#eef6ff',
                        lineHeight: 1.2,
                        wordBreak: 'keep-all',
                      }}
                    >
                      {item.corpName || item.ticker}
                    </Typography>
                    <Chip
                      size="small"
                      label={velocity.label}
                      sx={{
                        height: 22,
                        color: velocity.color,
                        background: velocity.bg,
                        border: `1px solid ${velocity.bd}`,
                        fontWeight: 900,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  </Box>

                  <Typography sx={{ mt: 0.4, fontSize: 11, color: neo.soft }}>
                    {item.ticker} · {item.market || '-'}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 1,
                    py: 0.45,
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    ...getUpdatedTone(item.lastEventAt),
                  }}
                >
                  {formatRelativeTime(item.lastEventAt)}
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 1.3,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                }}
              >
                <MiniStat label="현재가" value={formatNumber(item.currentPrice)} />
                <MiniStat label="변동률" value={formatPercent(item.changeRate)} tone={item.changeRate} />
                <MiniStat label="전일대비" value={formatSignedNumber(item.diffFromPrev)} tone={item.diffFromPrev} />
                <MiniStat label="거래량" value={formatNumber(item.cumVolume)} />
              </Box>

              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography sx={{ fontSize: 12, color: neo.muted }}>
                  거래대금 {formatTradingValue(item.cumTradingValue)}
                </Typography>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenQuick(item);
                  }}
                  sx={{
                    minWidth: 'auto',
                    px: 1.2,
                    py: 0.4,
                    borderRadius: 999,
                    color: neo.text,
                    borderColor: neo.border,
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                >
                  빠른 요약
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'auto',
        borderRadius: 1.75,
        border: `1px solid ${neo.border2}`,
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          minWidth: 980,
        }}
      >
        <thead>
          <tr style={{ background: 'rgba(7,17,31,.88)' }}>
            <Th>티커</Th>
            <Th>종목명</Th>
            <Th>시장</Th>
            <Th align="right">현재가</Th>
            <Th align="right">변동률</Th>
            <Th align="right">전일대비</Th>
            <Th align="right">거래량</Th>
            <Th align="right">거래대금</Th>
            <Th>최근 갱신</Th>
            <Th align="center">요약</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const changed = Boolean(updatedTickers[item.ticker]);

            return (
              <tr
                key={item.ticker}
                style={{
                  cursor: 'pointer',
                  background: changed ? 'rgba(79,124,255,.08)' : 'transparent',
                  transition: 'background .18s ease',
                }}
                onClick={() => onOpenQuick(item)}
              >
                <Td changed={changed}>{item.ticker}</Td>
                <Td changed={changed} strong>{item.corpName || item.ticker}</Td>
                <Td changed={changed}>{item.market || '-'}</Td>
                <Td align="right" changed={changed} strong>
                  {formatNumber(item.currentPrice)}
                </Td>
                <Td align="right" changed={changed} color={getChangeColor(item.changeRate)}>
                  {formatPercent(item.changeRate)}
                </Td>
                <Td align="right" changed={changed} color={getChangeColor(item.diffFromPrev)}>
                  {formatSignedNumber(item.diffFromPrev)}
                </Td>
                <Td align="right" changed={changed}>
                  {formatNumber(item.cumVolume)}
                </Td>
                <Td align="right" changed={changed}>
                  {formatTradingValue(item.cumTradingValue)}
                </Td>
                <Td changed={changed}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1.1,
                      py: 0.5,
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 900,
                      ...getUpdatedTone(item.lastEventAt),
                    }}
                  >
                    {formatRelativeTime(item.lastEventAt)}
                  </Box>
                </Td>
                <Td align="center" changed={changed}>
                  <MuiTooltip title="실시간 요약 보기">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenQuick(item);
                      }}
                      sx={{
                        color: neo.text,
                        border: `1px solid ${neo.border}`,
                        background: neo.pillBg,
                        width: 34,
                        height: 34,
                      }}
                    >
                      <OpenInFullIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
}